// Logging: one row per cued-round trial, one row per session for the
// preference items, plus an in-memory command log for debugging and for the
// week-12 agent interface.
//
// Three destinations, all built in from the start because the lecture-hall
// network cannot be trusted: a POST endpoint (Google Apps Script -> Sheet)
// with a retrying local queue, a CSV download, and a one-line summary the
// participant can paste into an Ed thread.

const ADJECTIVES = [
  'brisk', 'calm', 'clever', 'dim', 'eager', 'fuzzy', 'glad', 'hollow', 'keen',
  'lucky', 'merry', 'nimble', 'odd', 'plain', 'quick', 'rapid', 'shy', 'tidy',
  'upbeat', 'vivid', 'warm', 'zesty',
]
const ANIMALS = [
  'auk', 'bilby', 'crane', 'dingo', 'emu', 'finch', 'gecko', 'heron', 'ibis',
  'joey', 'kite', 'lorikeet', 'magpie', 'numbat', 'osprey', 'possum', 'quoll',
  'raven', 'skink', 'tern', 'urchin', 'wombat',
]

const QUEUE_KEY = 'nanojam.queue.v1'
const PID_KEY = 'nanojam.participant.v1'

export const TRIAL_COLUMNS = [
  'participant_id', 'session_ts', 'condition_order', 'interface', 'trial',
  'cue_sound', 'response_sound', 'correct', 'pad_width_px', 'pad_distance_px',
  'region', 'movement_time_ms', 'asynchrony_ms', 'device_class', 'ua_hash',
]

function pick(list, rnd) { return list[Math.floor(rnd() * list.length)] }

export function generateParticipantId() {
  const rnd = Math.random
  return `${pick(ADJECTIVES, rnd)}-${pick(ANIMALS, rnd)}`
}

function storedParticipantId() {
  try {
    let id = localStorage.getItem(PID_KEY)
    if (!id) { id = generateParticipantId(); localStorage.setItem(PID_KEY, id) }
    return id
  } catch {
    return generateParticipantId()   // private mode; id lives for this session only
  }
}

function deviceClass() {
  const touch = matchMedia('(pointer: coarse)').matches
  const w = Math.min(screen.width, screen.height)
  if (touch && w < 500) return 'phone'
  if (touch) return 'tablet'
  return 'desktop'
}

// Not a security measure -- just a stable short token so rows from the same
// browser can be grouped without storing a user agent string.
function uaHash() {
  const s = `${navigator.userAgent}|${navigator.platform}|${screen.width}x${screen.height}`
  let h = 2166136261
  for (let i = 0; i < s.length; i++) { h ^= s.charCodeAt(i); h = Math.imul(h, 16777619) }
  return (h >>> 0).toString(36)
}

export function createLogger({ params, endpoint = import.meta.env.VITE_LOG_ENDPOINT } = {}) {
  const sessionTs = new Date().toISOString()
  const device = deviceClass()
  const ua = uaHash()

  let participantId = params?.get('study.participantId') || storedParticipantId()
  if (params && !params.get('study.participantId')) params.set('study.participantId', participantId)

  const trials = []       // trial rows accumulated this session
  const sessions = []     // one row per completed session
  const commands = []     // in-memory only, never posted
  let queue = loadQueue()
  let flushing = false
  const listeners = new Set()

  function loadQueue() {
    try { return JSON.parse(localStorage.getItem(QUEUE_KEY) ?? '[]') } catch { return [] }
  }
  function saveQueue() {
    try { localStorage.setItem(QUEUE_KEY, JSON.stringify(queue.slice(-500))) } catch { /* full or blocked */ }
  }

  const enabled = () => !!params?.get('study.logging')

  function notify() { for (const fn of listeners) fn(status()) }

  function status() {
    return { participantId, queued: queue.length, trials: trials.length, endpoint: !!endpoint, enabled: enabled() }
  }

  async function flush() {
    if (flushing || !endpoint || queue.length === 0) return
    flushing = true
    try {
      const batch = queue.slice(0, 50)
      // Apps Script web apps reject preflight, so this is a "simple" request:
      // text/plain body, no custom headers, response opaque and ignored.
      await fetch(endpoint, {
        method: 'POST',
        mode: 'no-cors',
        headers: { 'Content-Type': 'text/plain;charset=utf-8' },
        body: JSON.stringify(batch),
      })
      queue = queue.slice(batch.length)
      saveQueue()
      notify()
      if (queue.length) setTimeout(flush, 400)
    } catch {
      setTimeout(flush, 5000)   // keep the rows; try again later
    } finally {
      flushing = false
    }
  }

  function enqueue(kind, row) {
    queue.push({ kind, ...row })
    saveQueue()
    notify()
    flush()
  }

  return {
    get participantId() { return participantId },
    get sessionTs() { return sessionTs },
    status,
    onStatus(fn) { listeners.add(fn); return () => listeners.delete(fn) },

    setParticipantId(id) {
      participantId = id || generateParticipantId()
      try { localStorage.setItem(PID_KEY, participantId) } catch { /* ignore */ }
      params?.set('study.participantId', participantId)
      notify()
    },

    newParticipantId() { this.setParticipantId(generateParticipantId()); return participantId },

    // Every command-API call lands here. Kept in memory only: free play must
    // make no network requests when logging is off.
    command(entry) {
      commands.push(entry)
      if (commands.length > 2000) commands.splice(0, 1000)
    },
    getCommands: () => [...commands],

    // One row per cued-round trial.
    trial(row) {
      const full = {
        participant_id: participantId,
        session_ts: sessionTs,
        device_class: device,
        ua_hash: ua,
        ...row,
      }
      trials.push(full)
      if (enabled()) enqueue('trial', full)
      else notify()
      return full
    },

    // One row per completed session (the preference items).
    session(row) {
      const full = {
        participant_id: participantId,
        session_ts: sessionTs,
        device_class: device,
        ua_hash: ua,
        ...row,
      }
      sessions.push(full)
      if (enabled()) enqueue('session', full)
      return full
    },

    getTrials: () => [...trials],
    getSessions: () => [...sessions],
    flush,

    toCSV(rows = trials) {
      if (rows.length === 0) return TRIAL_COLUMNS.join(',') + '\n'
      const cols = [...new Set([...TRIAL_COLUMNS, ...rows.flatMap(Object.keys)])]
      const esc = (v) => {
        if (v == null) return ''
        const s = String(v)
        return /[",\n]/.test(s) ? `"${s.replaceAll('"', '""')}"` : s
      }
      return [cols.join(','), ...rows.map((r) => cols.map((c) => esc(r[c])).join(','))].join('\n') + '\n'
    },

    downloadCSV(filename = `nanojam-${participantId}-${sessionTs.slice(0, 19).replaceAll(':', '')}.csv`) {
      const blob = new Blob([this.toCSV()], { type: 'text/csv' })
      const a = document.createElement('a')
      a.href = URL.createObjectURL(blob)
      a.download = filename
      a.click()
      setTimeout(() => URL.revokeObjectURL(a.href), 1000)
    },

    // The paste-into-Ed fallback.
    summaryLine() {
      const done = trials.filter((t) => t.movement_time_ms != null)
      if (done.length === 0) return `nanojam ${participantId}: no trials yet`
      const byIface = {}
      for (const t of done) (byIface[t.interface] ??= []).push(t)
      const parts = Object.entries(byIface).map(([iface, rows]) => {
        const mt = rows.map((r) => r.movement_time_ms).sort((a, b) => a - b)
        const median = mt[Math.floor(mt.length / 2)]
        const acc = Math.round((rows.filter((r) => r.correct).length / rows.length) * 100)
        return `${iface} ${Math.round(median)}ms ${acc}%`
      })
      return `nanojam ${participantId} (${device}) | ${parts.join(' | ')} | n=${done.length}`
    },
  }
}
